import { MoveHorizontal } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

/*
 * Generational pedigree tree — the structure is NEVER collapsed.
 * Columns = generations (parents / grandparents / great-grandparents).
 * On narrow screens the tree scrolls horizontally (finance-app style) with an
 * elegant fade + swipe affordance; row-spans keep the real tree alignment.
 */

const node = (p, path) => path.reduce((n, k) => (n ? n[k] : undefined), p);

const Cell = ({ n, label, size = "md" }) => (
  <div
    className={`rounded-xl border border-black/5 bg-white px-4 flex flex-col justify-center gap-0.5 min-w-0 ${
      size === "lg" ? "py-5" : size === "md" ? "py-3" : "py-2"
    } ${n?.name ? "" : "opacity-60"}`}
  >
    <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#1D4D3B]">{label}</span>
    <span className={`font-medium text-[#0A0A0A] truncate ${size === "lg" ? "text-base" : size === "md" ? "text-sm" : "text-[13px]"}`}>
      {n?.name || "—"}
    </span>
    {(n?.studbook || n?.year) && (
      <span className="text-xs text-[#737373] truncate">{[n.studbook, n.year].filter(Boolean).join(" · ")}</span>
    )}
  </div>
);

export const PedigreeTree = ({ pedigree }) => {
  const { t } = useTranslation();
  const scrollRef = useRef(null);
  const [hint, setHint] = useState(false);

  const GP = [["sire", "sire"], ["sire", "dam"], ["dam", "sire"], ["dam", "dam"]];
  const GGP = GP.flatMap((p) => [[...p, "sire"], [...p, "dam"]]);
  const hasGGP = pedigree && GGP.some((p) => node(pedigree, p)?.name);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const update = () => setHint(el.scrollWidth > el.clientWidth + 8 && el.scrollLeft < el.scrollWidth - el.clientWidth - 8);
    update();
    el.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      el.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [hasGGP]);

  if (!pedigree) return null;
  const sireDam = (p) => (p[p.length - 1] === "sire" ? t("pub.sire") : t("pub.dam"));

  /* explicit grid placement — row/col per node so the tree NEVER scrambles */
  const cells = hasGGP
    ? [
        { path: ["sire"], col: 1, row: 1, span: 4, size: "lg" },
        { path: ["dam"], col: 1, row: 5, span: 4, size: "lg" },
        ...GP.map((p, i) => ({ path: p, col: 2, row: i * 2 + 1, span: 2, size: "md" })),
        ...GGP.map((p, i) => ({ path: p, col: 3, row: i + 1, span: 1, size: "sm" })),
      ]
    : [
        { path: ["sire"], col: 1, row: 1, span: 2, size: "lg" },
        { path: ["dam"], col: 1, row: 3, span: 2, size: "lg" },
        ...GP.map((p, i) => ({ path: p, col: 2, row: i + 1, span: 1, size: "md" })),
      ];

  return (
    <div className="relative" data-testid="pedigree-tree">
      <div ref={scrollRef} className="overflow-x-auto pb-2 -mx-6 px-6 lg:mx-0 lg:px-0">
        <div
          className={`grid gap-3 ${
            hasGGP
              ? "min-w-[820px] grid-cols-3 grid-rows-[repeat(8,minmax(0,1fr))]"
              : "min-w-[540px] grid-cols-2 grid-rows-[repeat(4,minmax(0,1fr))]"
          }`}
        >
          {cells.map(({ path, col, row, span, size }) => (
            <div
              key={path.join(".")}
              className="grid"
              style={{ gridColumnStart: col, gridRowStart: row, gridRowEnd: `span ${span}` }}
            >
              <Cell n={path.length === 1 ? pedigree[path[0]] : node(pedigree, path)} label={sireDam(path)} size={size} />
            </div>
          ))}
        </div>
      </div>

      {/* swipe affordance — only while more tree remains off-screen */}
      <div
        aria-hidden="true"
        className={`pointer-events-none absolute inset-y-0 -right-6 lg:right-0 w-20 bg-gradient-to-l from-white to-transparent transition-opacity duration-300 ${
          hint ? "opacity-100" : "opacity-0"
        }`}
      />
      <span
        data-testid="pedigree-swipe-hint"
        className={`pointer-events-none absolute bottom-4 right-0 inline-flex items-center gap-1.5 px-3 h-7 rounded-full bg-[#0A0A0A]/85 backdrop-blur-sm text-white text-[11px] font-medium transition-opacity duration-300 ${
          hint ? "opacity-100" : "opacity-0"
        }`}
      >
        <MoveHorizontal className="w-3.5 h-3.5" aria-hidden="true" />
        {t("ped.swipe")}
      </span>
    </div>
  );
};
